import React, { useEffect, useState } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Navbar from "../componenets/Navbar";
import Footer from "../componenets/Footer";
import { Users, FileText, ShieldCheck, Lock, CheckCircle, MapPin, Sparkles } from "lucide-react";
import "../App.css";

interface AgentProfile {
  uid: string;
  fullName: string;
  company: string;
  licenseNumber: string;
  email: string;
  status: string;
  paymentStatus: string;
  location?: string;
}

const Agents = () => {
  const [agents, setAgents] = useState<AgentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPaidUser, setIsPaidUser] = useState(false);

  useEffect(() => {
    // 1. Monitor Auth State & Check User Permissions
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsLoggedIn(true);
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          // Access restricted data only if user document has paymentStatus: "paid"
          setIsPaidUser(userSnap.data().paymentStatus === "paid");
        }
      } else {
        setIsLoggedIn(false);
        setIsPaidUser(false);
      }
    });

    // 2. Fetch Verified & Paid Agents for the public list
    const fetchAgents = async () => {
      try {
        const q = query(
          collection(db, "users"),
          where("role", "==", "agent"),
          where("status", "==", "approved"), 
          where("paymentStatus", "==", "paid")
        );
        const querySnapshot = await getDocs(q);
        const agentList = querySnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data()
        } as AgentProfile));
        setAgents(agentList);
      } catch (error) {
        console.error("Error fetching agents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
    return () => unsubscribe();
  }, []);

  const handleLoginRedirect = () => {
    window.location.href = "/signin";
  };

  const handleUpgradeClick = () => {
    alert("Subscription feature is currently handled manually. Please contact support to upgrade your account.");
  };

  return (
    <div className="agents-page">
      <Navbar />
      <div className="agents-container">
        <header className="agents-header">
          <div className="agents-brand">
            <div className="agents-logo">
              <Users className="icon-white" />
            </div>
            <div>
              <h1 className="agents-title">Verified Clearing Agents</h1>
              <p className="agents-subtitle">
                Authorized professionals verified for compliance and active status.
              </p>
            </div>
          </div>
        </header>

        <div className="agents-grid">
          <div className="agents-card">
            <div className="agents-icon-text">
              <FileText className="agents-icon" />
              <div>
                <h3 className="agents-card-title">Compliance First</h3>
                <p className="agents-card-text">
                  All listed agents have submitted valid CHA licenses verified by our administration team.
                </p>
              </div>
            </div>
          </div>
          <div className="agents-card">
            <div className="agents-icon-text">
              <ShieldCheck className="agents-icon" />
              <div>
                <h3 className="agents-card-title">Trusted Network</h3>
                <p className="agents-card-text">
                  Only active, fee-paying members are displayed to ensure you connect with available professionals.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="agents-card">
          <h2 className="agents-section-title">Official Directory</h2>

          {!isPaidUser && (
            <div className="directory-lock-box">
              <Lock size={24} color="#e74c3c" />
              <div className="lock-content">
                <p className="lock-text-main">
                  {isLoggedIn 
                    ? "Full agent details are exclusive to premium members." 
                    : "Login and subscribe to unlock the full directory."}
                </p>
                
                <div className="lock-actions">
                  {!isLoggedIn ? (
                    <button className="agents-login-btn" onClick={handleLoginRedirect}>
                      Login to Access
                    </button>
                  ) : (
                    <button className="upgrade-btn-premium" onClick={handleUpgradeClick}>
                      <Sparkles size={18} /> Contact Admin to Upgrade
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="table-wrapper">
            {loading ? (
              <div className="loader">Updating Directory...</div>
            ) : (
              <table className="agents-table">
                <thead>
                  <tr>
                    <th>Agent Name</th>
                    <th>Company</th>
                    <th>Location</th>
                    <th>License No.</th>
                    <th>Contact</th>
                    <th>Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.length > 0 ? (
                    agents.map((agent) => (
                      <tr key={agent.uid} className={!isPaidUser ? "row-locked" : ""}>
                        <td className="bold-text">
                          {isPaidUser ? agent.fullName : "🔒 Restricted"}
                        </td>
                        <td>{agent.company || "Independent"}</td>
                        <td className="location-cell">
                          <MapPin size={14} className="inline-icon" /> {agent.location || "Not Specified"}
                        </td>
                        <td className="license-cell">{agent.licenseNumber}</td>
                        <td>
                          {isPaidUser ? (
                            <a href={`mailto:${agent.email}`} className="email-link">{agent.email}</a>
                          ) : (
                            <span className="locked-badge">Locked</span>
                          )}
                        </td>
                        <td>
                          <span className="badge-verified">
                            <CheckCircle size={14} /> Verified
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="no-data">No active agents found matching criteria.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <footer className="agents-disclaimer-box">
          <p>
            <strong>Note:</strong> While we verify licenses, users are encouraged to perform their own due diligence 
            before signing service agreements with any agent.
          </p>
        </footer>
      </div>
      <Footer />
    </div>
  );
};

export default Agents;